/**
 * Traceability Hook
 * Manages traceability graph data
 */

import { useQuery } from '@tanstack/react-query';
import { useAuthToken } from './use-auth';

// Types
export interface TraceabilityNode {
  id: string;
  type: 'goal' | 'persona' | 'useCase' | 'journey' | 'baseSpec' | 'contract' | 'technicalSpec' | 'story' | 'evidence';
  label: string;
  status: string;
  metadata?: Record<string, unknown>;
}

export interface TraceabilityEdge {
  source: string;
  target: string;
  type: string;
  metadata?: Record<string, unknown>;
}

export interface TraceabilityGap {
  type: string;
  severity: 'high' | 'medium' | 'low';
  message: string;
  sourceId?: string;
  sourceType?: string;
  targetType?: string;
}

export interface TraceabilityGraph {
  nodes: TraceabilityNode[];
  edges: TraceabilityEdge[];
  gaps: TraceabilityGap[];
  coverage: {
    goals: number;
    personas: number;
    useCases: number;
    journeys: number;
    baseSpecs: number;
    contracts: number;
    technicalSpecs: number;
    stories: number;
    evidence: number;
  };
}

// Get traceability graph
export function useTraceability(projectId: string) {
  const token = useAuthToken();
  
  return useQuery({
    queryKey: ['traceability', projectId],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${projectId}/traceability`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch traceability graph');
      const data = await response.json();
      return data.data as TraceabilityGraph;
    },
    enabled: !!projectId && !!token,
  });
}
